import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    useTransition,
} from 'react';
import { Check } from 'lucide-react';
import { ArrowLeftLine } from '@archipro-design/icons';
import { useTracker } from '@archipro-website/tracker';
import { useCountryContext } from '@archipro-website/localisation';
import { Spinner } from '@rocco/components/spinner';
import { cn } from '@rocco/utils/cn';
import { useEnquirySession } from '~/modules/enquiry/hooks/use-enquiry';
import { useRootData } from '~/modules/root';
import ForgotPasswordForm from '~/modules/user/component/forgot-password/ForgotPasswordForm';
import LoginSignUpForm from '~/modules/user/component/login-signup-form/LoginSignUpForm';
import { useBypassForcedSocial } from '~/modules/user/hook/use-bypass-forced-social';
import type { LoginFlow } from '~/modules/user/hook/use-login-flow';
import { usePublicUser } from '~/modules/user/hook/use-public-user';
import type { AuthSource } from '~/modules/user/type';
import { getLoginPolicy } from '~/modules/user/util/login-policy';
import { trackAuthEvent } from '~/modules/user/util/track-auth-event';
import { EMAIL_INPUT } from '~/modules/validation/util/form';
import type { PublicUser } from '~/routes/remix-api.user.public-user';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow-r.svg';
// The hook's own path, not the `@modules/user` barrel: that barrel re-exports
// LoginPage, which renders this panel, and the cycle that closes leaves one of
// the two undefined at module-evaluation time depending on entry order.
import { useUserLoggedIn } from '@modules/user/hook/use-user';
import { usePasskeyEnrolOffer } from '~/modules/user/passkey/use-passkey-enrol-offer';
import type { PasskeyEnrolmentMint } from '~/modules/user/passkey/passkey-enrol-mint';
import { type PasskeyDeviceHint } from '~/modules/user/passkey/passkey-device-hint';
import { matchPasskeyDeviceToHost } from '~/modules/user/passkey/passkey-device-match';
import { markPasskeyOfferSkipped } from '~/modules/user/passkey/passkey-device-hint';
import { logPasskeySuppressed } from '~/modules/user/passkey/passkey-diagnostics';
import { useDeviceBrowserContext } from '@archipro-design/aria';
import { handleAuthRedirect } from '~/modules/root/util/handle-auth-redirect';
import { getPostAuthDestination } from '~/modules/root/util/post-auth-redirect';
import { useFeature } from '~/modules/root';
import { AuthModalSocialButtons } from './AuthModalSocialButtons';
import { PasskeyLoginStep } from './PasskeyLoginStep';
import type { PasskeyLoginFallbackReason } from './PasskeyLoginStep';
import { SecureSignInOffer } from './SecureSignInOffer';
import {
    getAuthIntentCopy,
    PASSKEY_ATTEMPT_FAILED_NOTICE,
    PASSKEY_RETIRED_NOTICE,
    welcomeBackGreeting,
} from './auth-intent-copy';
import { GateHeading, GateSubcopy } from './GateHeading';

type AuthPanelStep =
    | 'passkey-login'
    | 'initial'
    | 'details'
    | 'forgot-password'
    | 'secure-sign-in'
    | 'redirecting';

export interface AuthPanelProps {
    authSource: AuthSource;
    flow?: LoginFlow;
    redirect?: string;
    allowExternalUrls?: boolean;
    skipPostSignUp?: boolean;
    /**
     * Complete auth in place without the post-auth page redirect. Used by the
     * enquiry gate so password/email-code auth finishes in-modal and the
     * enquiry is resubmitted without a reload.
     */
    skipAuthRedirect?: boolean;
    /**
     * Stay on the email-first step even when this device holds a passkey.
     *
     * Step 0 opens the passkey ceremony on mount, which raises the platform's
     * own Touch ID / Windows Hello dialog. In a modal the reader asked for
     * that by opening it. A panel that is simply part of the page — the
     * sign-up band every detail page closes on — has asked for nothing, so
     * detecting there greets every enrolled visitor with an OS prompt they
     * did not invite, on every page they land on. Those hosts set this and
     * offer the passkey behind a click instead.
     */
    skipPasskeyStep?: boolean;
    /** Overrides the intent registry headline. */
    header?: string;
    /** Overrides the intent registry subcopy. */
    subtitle?: string;
    /** Contextual line shown above the form (e.g. enquiry gating message). */
    message?: string;
    defaultPublicUser?: PublicUser;
    /** Fired on in-panel auth completion; reports whether a signup happened. */
    onAuthSuccess: (result?: { isSignUp: boolean }) => void;
    /**
     * Renders a back arrow on the initial step, for a host whose gate has
     * steps before it — the Get Matched wizard's questions, say. The later
     * steps keep their own arrows, which return within the panel.
     */
    onBack?: () => void;
}

const POLICY_LINKS = {
    AU: 'https://archipro.com.au/privacy-policy',
    NZ: 'https://archipro.co.nz/privacy-policy',
    US: 'https://archiproamerica.com/privacy-policy',
};

/**
 * Enforce getLoginPolicy: staff `@archipro.co.nz` accounts sign in with Google
 * and are offered neither password nor email code.
 *
 * On for the same reason the legacy path enforces it — a revamped login that
 * dropped the policy would be a way around it. {@link getLoginPolicy} is
 * already host-gated, so this only binds on the market domains; `+tagged`
 * addresses keep password login for testing.
 */
const ENFORCE_LOGIN_POLICY = true;

/**
 * Right-panel step machine of the revamped auth experience (BC-4826):
 * email-first initial step, account detection, and the login ("Welcome back")
 * or sign-up form, plus the forgot-password and passwordless code-entry steps.
 * Rendered inside the two-pane AuthModal shell, and reused in the enquiry
 * gate's right pane so guests can authenticate in-modal.
 */
export const AuthPanel = (props: AuthPanelProps) => {
    const {
        authSource,
        flow,
        redirect,
        allowExternalUrls,
        skipPostSignUp,
        skipAuthRedirect,
        skipPasskeyStep,
        header,
        subtitle,
        message,
        defaultPublicUser,
        onAuthSuccess,
        onBack,
    } = props;

    const tracker = useTracker();
    const { env } = useRootData();
    const { byCountry } = useCountryContext();
    const { enquirySession } = useEnquirySession();
    const { bypassForcedSocial } = useBypassForcedSocial(env.stage);
    const { publicUser, setPublicUser, handleUserExists } = usePublicUser(
        authSource,
        defaultPublicUser,
    );

    const [step, setStep] = useState<AuthPanelStep>(
        defaultPublicUser ? 'details' : 'initial',
    );
    const [emailValue, setEmailValue] = useState('');
    /**
     * A caller that already knows the visitor — the enquiry gates, which
     * detected them from the email on the form — has effectively completed the
     * initial step on their behalf, so seed it as though the email had been
     * typed here. Without this the details step still renders the social block
     * and the "or continue with your email" divider, producing a screen that is
     * both step 0 and the welcome-back step at once, and it hides the
     * "Not me?" escape hatch.
     */
    const [submittedEmail, setSubmittedEmail] = useState<string | undefined>(
        defaultPublicUser?.email,
    );
    const [emailError, setEmailError] = useState<string>();
    /** Addresses already reported as entered, so a return to step 0 re-counts nothing. */
    const emailInputReportedRef = useRef(new Set<string>());
    const [enteredViaEmail, setEnteredViaEmail] = useState(
        Boolean(defaultPublicUser),
    );
    const [forcedSocialBypassed, setForcedSocialBypassed] = useState(false);
    const [forgotCompleted, setForgotCompleted] = useState(false);
    /** True while the passwordless code-entry step is showing (drives headline/subcopy). */
    const [emailCodeActive, setEmailCodeActive] = useState(false);
    /** True while a code send/resend request is in flight (spinner on the subcopy). */
    const [emailCodeSending, setEmailCodeSending] = useState(false);

    // Secure Sign-In (BC-4831): in redirect flows the form hands over the
    // post-auth navigation as a `finish` closure; when this device qualifies,
    // the enrolment ask renders as a step and `finish` runs on resolve —
    // [details/code-entry] → [secure-sign-in?] → redirect.
    const { user, isLoggedIn } = useUserLoggedIn();
    const { decideOffer: decidePasskeyOffer } = usePasskeyEnrolOffer();
    const pendingFinishRef = useRef<(() => void) | null>(null);
    const [enrolMint, setEnrolMint] = useState<PasskeyEnrolmentMint>();
    /** The step as of this render, for the unmount path to read on its way out. */
    const stepRef = useRef(step);
    stepRef.current = step;
    /**
     * Whether the ask on screen has registered a credential.
     *
     * Reported by the ask rather than read back off the device hint: a hint
     * saying `enrolled` cannot say *who* enrolled or *when* within the ask, and
     * a sign-up is deliberately offered enrolment on a browser that already
     * holds someone else's passkey. Only the ask itself knows.
     */
    const askEnrolledRef = useRef(false);
    /** Claims the pending hand-over, so it can only ever be run once. */
    const takePendingFinish = useCallback(() => {
        const pending = pendingFinishRef.current;
        pendingFinishRef.current = null;
        return pending;
    }, []);

    /**
     * Hands over to the post-auth navigation behind a holding step.
     *
     * `finish` always ends in a full page load — the form only defers to
     * `onAuthComplete` on the redirect flows — and that load is several round
     * trips away: the session has to reach the client, the auth event is
     * awaited, and the redirect itself is deferred by a browser-specific
     * timeout. Nothing the panel could still be showing belongs on screen for
     * that wait. The form is accepted and inert, and a Secure Sign-In offer the
     * server refused renders nothing at all — which on a host with no dialog to
     * close leaves the card standing empty at its full height. The spinner is
     * what the reader is owed until the page changes under them.
     */
    const finishWithHoldingStep = useCallback(() => {
        const pending = takePendingFinish();
        if (!pending) return;
        setStep('redirecting');
        pending();
    }, [takePendingFinish]);

    /**
     * What leaving mid-flow means, kept current so the unmount effect below can
     * hold empty deps and fire on the way out and nowhere else.
     *
     * A hand-over outlives the panel, because auth already succeeded.
     * Everything between the credentials being accepted and the page changing
     * is dismissible — the modal keeps its close control, and the enrolment ask
     * is meant to be escapable. But dismissing cannot un-authenticate anyone:
     * whatever is behind the panel is still the logged-out render of a page
     * they are now signed in to. Leaving without running `finish` strands them
     * there, so the navigation happens on the way out.
     */
    const abandonRef = useRef<() => void>(() => {});
    abandonRef.current = () => {
        const pending = takePendingFinish();
        if (!pending) return;
        // Walking away from the ask is a decline, and the cadence only counts
        // the declines it is told about — so an offer abandoned this way would
        // come back at the next login as though it had never been made. This is
        // the reading the gate already applies when its shell is closed from
        // this step; the ask reached through a modal deserves the same.
        //
        // Unless the ask was answered on the way past. It holds a success
        // frame after the credential is registered and before it resolves, so
        // a reader who leaves during that frame has enrolled, not declined —
        // and logging it as a decline would put both events on one journey.
        if (stepRef.current === 'secure-sign-in' && !askEnrolledRef.current) {
            markPasskeyOfferSkipped();
            tracker.log('PasskeyEnrolSkipped', {
                url: new URL(window.location.href),
                data: { Source: authSource, ExtraData: 'modal_closed' },
            });
        }
        pending();
    };

    useEffect(() => () => abandonRef.current(), []);
    const handleAuthComplete = useCallback(
        (finish: () => void, meta?: { isSignUp: boolean }) => {
            // The hold goes up before the decision, not after it. Deciding now
            // includes minting the challenge the ask would spend, so it is a
            // round trip — and the form it would otherwise leave on screen has
            // been accepted and does nothing.
            setStep('redirecting');
            // Claimed before the decision is awaited, not when the ask wins it:
            // the reader can dismiss during the wait, and the cleanup above is
            // what completes for them when they do.
            pendingFinishRef.current = finish;
            // Platform-authenticator detection is async, so the redirect waits
            // on the answer rather than assuming this device cannot be asked.
            // `finish` is what navigates an authenticated user away, so a failed
            // decision must still run it — never strand them on the form.
            // `memberID` is best-effort here: on redirect flows the session
            // has not revalidated yet, so it is often unknown and the hint keeps
            // suppressing exactly as it did before. A sign-up needs no ID — the
            // account is new by definition.
            //
            // It has to come from `isLoggedIn` rather than from `'ID' in user`:
            // the logged-out user is `{ __typename: 'Guest', ID: 0 }`, so the
            // `in` check passes and hands the cadence member 0 — a member who
            // enrolled nothing, so every device that already held a passkey
            // read as somebody else's and was asked to enrol again.
            void decidePasskeyOffer({
                memberID: isLoggedIn ? user.ID : undefined,
                isNewAccount: !!meta?.isSignUp,
            })
                .then((offer) => {
                    if (offer) {
                        // The ask owns the hand-over from here.
                        askEnrolledRef.current = false;
                        setEnrolMint(offer);
                        setStep('secure-sign-in');
                        return;
                    }
                    finishWithHoldingStep();
                })
                .catch(() => finishWithHoldingStep());
        },
        [decidePasskeyOffer, isLoggedIn, user, finishWithHoldingStep],
    );

    // Step-0 passkey login (BC-4832): a device holding an enrolled hint for
    // this relying party opens on the passkey step instead of the email-first
    // step. Pre-paint so the regular step never flashes; the hint alone gates
    // it (an enrolled hint implies platform-authenticator support), and any
    // ceremony refusal falls back to the regular step.
    const { isFirefox } = useDeviceBrowserContext();
    const passkeyFlagEnabled = useFeature('revamp_2026');
    const passkeyHintRef = useRef<PasskeyDeviceHint | null>(null);
    const passkeyDetected = useRef(false);
    const [passkeyFallback, setPasskeyFallback] =
        useState<PasskeyLoginFallbackReason>();
    useLayoutEffect(() => {
        if (passkeyDetected.current) return;
        passkeyDetected.current = true;
        if (skipPasskeyStep) {
            logPasskeySuppressed(
                'Passkey step 0',
                'the host renders this panel as part of the page, so a ' +
                    'ceremony here would be an uninvited platform prompt',
            );
            return;
        }
        if (!passkeyFlagEnabled) {
            logPasskeySuppressed('Passkey step 0', 'revamp_2026 is off');
            return;
        }
        // A host that already captured a NEW email (enquiry gate signup) is
        // creating an account — the enrolled device's passkey would log into
        // a different one. Existing-account hosts keep the fast path.
        if (defaultPublicUser?.type === 'new') {
            logPasskeySuppressed(
                'Passkey step 0',
                'this email has no account yet, so the passkey would sign in ' +
                    'as someone else',
            );
            return;
        }
        const match = matchPasskeyDeviceToHost();
        if (!match.recognised) {
            logPasskeySuppressed('Passkey step 0', match.reason);
            return;
        }
        passkeyHintRef.current = match.hint;
        setStep('passkey-login');
    }, [passkeyFlagEnabled, defaultPublicUser?.type, skipPasskeyStep]);

    const handlePasskeyLoginSuccess = useCallback(() => {
        if (skipAuthRedirect) {
            // In-place flows (enquiry gate) never navigate; nothing else
            // emits the login event for them.
            void trackAuthEvent({
                tracker,
                event: 'user_login',
                provider: 'Passkey',
                authSource,
            });
            onAuthSuccess({ isSignUp: false });
            return;
        }
        // Track, then navigate directly — the reload refreshes every loader,
        // so a pre-reload revalidation pass (the AuthContext.authRedirect
        // contract) would only run them all twice. The destination still goes
        // through getPostAuthDestination (redirect sanitisation,
        // encodedRedirect, login-route handling); passkey login is always a
        // returning member, so onboarding routing never applies. Deliberately
        // no onAuthSuccess: the modal stays open (still animating) until the
        // navigation lands, so a stale logged-out page never shows.
        void (async () => {
            await trackAuthEvent({
                tracker,
                event: 'user_login',
                provider: 'Passkey',
                authSource,
            });
            const { pathname, search } = window.location;
            const finalDestination = getPostAuthDestination({
                pathname,
                search,
                redirect,
                allowExternalUrls,
                skipPostSignUp: true,
                encodedRedirect: new URLSearchParams(search).get(
                    'encodedRedirect',
                ),
                buildHubFeatureEnabled: false,
                hasBuildHubs: false,
            });
            await handleAuthRedirect({
                newURL: finalDestination,
                authSource,
                isFirefox,
            });
        })();
    }, [
        skipAuthRedirect,
        tracker,
        authSource,
        onAuthSuccess,
        redirect,
        allowExternalUrls,
        isFirefox,
    ]);

    /**
     * Leave step 0 for the regular flow, remembering why.
     *
     * The reason is what stops the panel pitching a free account at somebody it
     * has just addressed by name: the step only opens for a device we recognise,
     * so whatever went wrong, the reader on the other side of it has an account.
     */
    const handlePasskeyLoginFallback = useCallback(
        (reason: PasskeyLoginFallbackReason) => {
            setPasskeyFallback(reason);
            setStep(defaultPublicUser ? 'details' : 'initial');
        },
        [defaultPublicUser],
    );

    const intentCopy = getAuthIntentCopy(authSource);

    /**
     * Steps that mount a form (`details`, `forgot-password`) cost enough to
     * render that committing them urgently drops a frame, which lands as a
     * stutter right before the sheet resizes around the new step. Marking the
     * change as a transition lets React build it off the critical path; the
     * reader keeps looking at the step they came from until it is ready.
     */
    const [isEnteringStep, startStepTransition] = useTransition();

    // The arrow's spinner has to span both halves of the wait — the account
    // lookup and the render it triggers — or it stops while the reader is still
    // looking at the step they submitted from.
    const detecting =
        submittedEmail !== undefined && publicUser?.email !== submittedEmail;

    useEffect(() => {
        if (
            submittedEmail !== undefined &&
            publicUser?.email === submittedEmail &&
            step === 'initial'
        ) {
            startStepTransition(() => {
                setEnteredViaEmail(true);
                setStep('details');
            });
        }
    }, [submittedEmail, publicUser?.email, step]);

    const derivedFlow = useMemo((): LoginFlow => {
        const isPostEnquiry = !!flow?.includes('post-enquiry');
        if (publicUser?.type === 'new') {
            return isPostEnquiry ? 'post-enquiry-sign-up' : 'sign-up';
        }
        if (publicUser?.type === 'existing') {
            return isPostEnquiry ? 'post-enquiry-login' : 'login';
        }
        return null;
    }, [publicUser?.type, flow]);

    const isSignUpFlow =
        derivedFlow === 'sign-up' || derivedFlow === 'post-enquiry-sign-up';
    const isLoginFlow =
        derivedFlow === 'login' || derivedFlow === 'post-enquiry-login';
    const knownEmail = submittedEmail ?? publicUser?.email;

    /**
     * Tells the host the panel is done — but only where being done means
     * anything to it.
     *
     * On the in-place flows that is the whole contract: the gate has to move to
     * its own next step and nothing else will tell it to. On the redirect flows
     * the host's answer is to close, and closing uncovers the page the reader
     * authenticated from — still rendered logged out, and still there for as
     * long as the redirect takes to arrive, which is a session revalidation and
     * a tracking round trip away. They watch themselves get signed out. The
     * panel holds its spinner instead and the page load takes the surface down
     * with it, which is what the step-0 passkey path already does.
     */
    const handleFormSuccess = useCallback(() => {
        if (!skipAuthRedirect) return;
        onAuthSuccess({ isSignUp: isSignUpFlow });
    }, [skipAuthRedirect, onAuthSuccess, isSignUpFlow]);

    const loginPolicy = getLoginPolicy(knownEmail ?? '');
    const policyForcesGoogle =
        ENFORCE_LOGIN_POLICY && isLoginFlow && loginPolicy.forceGoogle;

    const forcedSocial =
        policyForcesGoogle ||
        (publicUser?.type === 'existing' &&
            publicUser.socialLoginOptions.length === 1 &&
            !publicUser.archiproLoginOption &&
            !forcedSocialBypassed &&
            !bypassForcedSocial);
    const forcedProvider = policyForcesGoogle
        ? 'Google'
        : publicUser?.socialLoginOptions[0];

    /** Blur only validates; proceeding is always the explicit arrow/Enter submit. */
    const handleEmailBlur = () => {
        const email = emailValue.trim();
        if (!email) {
            setEmailError(undefined);
            return;
        }
        setEmailError(
            EMAIL_INPUT.safeParse(email.toLowerCase()).success
                ? undefined
                : 'Please enter a valid email address',
        );
    };

    const emailReady = EMAIL_INPUT.safeParse(
        emailValue.trim().toLowerCase(),
    ).success;

    /** Arrow/Enter: validate, then run the platform account check and step to the result. */
    const handleEmailSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const email = emailValue.trim().toLowerCase();
        const parsed = EMAIL_INPUT.safeParse(email);
        if (!parsed.success) {
            setEmailError('Please enter a valid email address');
            return;
        }
        setEmailError(undefined);
        // One report per email. "Use a different email" returns the reader to
        // this step, and re-submitting the address they arrived with counted a
        // second entry for one journey.
        // Same normalisation the form below uses, so the two suppression sets
        // agree on case and whitespace rather than counting `A@x.com` and
        // `a@x.com` differently depending on which one reported it.
        const normalised = email.trim().toLowerCase();
        if (!emailInputReportedRef.current.has(normalised)) {
            emailInputReportedRef.current.add(normalised);
            trackAuthEvent({
                tracker,
                event: 'AuthEmailInput',
                authSource,
                provider: 'EmailPassword',
            });
        }
        setSubmittedEmail(email);
        handleUserExists(email);
    };

    const handleUseDifferentEmail = () => {
        startStepTransition(() => {
            setStep('initial');
            setSubmittedEmail(undefined);
            setEnteredViaEmail(false);
            setForcedSocialBypassed(false);
            setEmailCodeActive(false);
            setPublicUser(defaultPublicUser);
            // Whoever is typing a different address is not the reader whose
            // passkey was refused, so the note explaining that refusal stops
            // applying to the screen they are on.
            setPasskeyFallback(undefined);
        });
    };

    /**
     * The greeting, when the step has one to give: a recognised account (even
     * without a stored first name — the account is confirmed, so falling
     * through to the registry's sign-up pitch would contradict what we just
     * learned) or an enquiry session that carries the visitor's name. Kept
     * apart from the headline so the subcopy can tell "greeting above" from
     * "registry pitch above".
     */
    const detailsGreeting = useMemo(() => {
        if (publicUser?.type === 'existing') {
            return welcomeBackGreeting(publicUser.firstName);
        }
        if (defaultPublicUser?.type === 'new' && enquirySession?.name) {
            return `Welcome, ${enquirySession.name}`;
        }
        return undefined;
    }, [publicUser, defaultPublicUser?.type, enquirySession?.name]);

    const detailsHeadline = detailsGreeting ?? (header || intentCopy.headline);

    // The headline names who, so the line beneath carries what they came to
    // do — but only under a greeting. Under the registry's own pitch headline
    // the action line would restate it almost word for word ("Create a free
    // account to Keep Reading" over "Create your free account to keep
    // reading."). Keyed off the detected account type, so it leaves the
    // log-in line behind when a changed email turns out to be unknown.
    const detailsSubcopy =
        message ??
        subtitle ??
        (isSignUpFlow
            ? detailsGreeting
                ? intentCopy.newAction
                : 'It takes a moment, and keeps your enquiries, boards and saved products in one place.'
            : intentCopy.returningAction);

    /**
     * One muted line accounting for the step the reader did not ask to be on.
     *
     * Nothing for `user-choice` — they pressed the button, and explaining their
     * own click back to them is noise. Muted rather than destructive, too: they
     * did nothing wrong, and red at the top of a login screen reads as
     * "your credentials were rejected".
     */
    const renderPasskeyNotice = () => {
        if (!passkeyFallback || passkeyFallback === 'user-choice') return null;

        return (
            <p role="status" className="mt-3 text-[0.8125rem] text-[#828282]">
                {passkeyFallback === 'credential-retired'
                    ? PASSKEY_RETIRED_NOTICE
                    : PASSKEY_ATTEMPT_FAILED_NOTICE}
            </p>
        );
    };

    const renderInitialStep = () => (
        <>
            {onBack && (
                <button
                    type="button"
                    aria-label="Back"
                    onClick={onBack}
                    className="mb-4 flex size-8 items-center justify-center self-start rounded-[0.75rem] text-[#181818] hover:bg-black/5"
                >
                    <ArrowLeftLine className="size-5" />
                </button>
            )}
            {/* On the fallback the greeting and its line replace whatever the
                host asked for, the same precedence the details step already
                gives `detailsGreeting`: a caller's headline is written for a
                stranger, and this reader is not one. */}
            <GateHeading>
                {passkeyFallback
                    ? welcomeBackGreeting(passkeyHintRef.current?.firstName)
                    : header || intentCopy.headline}
            </GateHeading>
            <GateSubcopy>
                {passkeyFallback
                    ? intentCopy.returningAction
                    : subtitle || intentCopy.subcopy}
            </GateSubcopy>
            {renderPasskeyNotice()}
            <AuthModalSocialButtons
                className="mt-10 pt-1"
                loginOptions={publicUser?.socialLoginOptions}
                authSource={authSource}
                redirect={redirect}
                skipPostSignUp={!!skipPostSignUp}
            />
            <div className="mt-6 flex items-center justify-center gap-3 opacity-60">
                <span className="h-px w-14 bg-[#9d9d9d]" />
                <span className="text-[1.0625rem] tracking-[-0.03em] text-[#828282]">
                    or continue with your email
                </span>
                <span className="h-px w-14 bg-[#9d9d9d]" />
            </div>
            <form onSubmit={handleEmailSubmit} className="mt-6" noValidate>
                <div className="flex h-14 items-center justify-between gap-2 rounded-[1.375rem] bg-[#f5f5f5] pr-1.75 pl-7.5">
                    <input
                        type="email"
                        value={emailValue}
                        onChange={(e) => setEmailValue(e.target.value)}
                        onBlur={handleEmailBlur}
                        placeholder="your@email.com"
                        autoComplete="username"
                        autoCapitalize="off"
                        // Holds the same 16px floor as the gate fields — see
                        // GATE_FIELD_INPUT_CLASS for why rem alone will not.
                        className="min-w-0 flex-1 bg-transparent text-[max(1.1875rem,16px)] text-[#181818] placeholder:text-[#828282] focus:outline-none md:text-[1.1875rem] [&:-internal-autofill-selected]:shadow-[inset_0_0_0_1000px_#f5f5f5] [&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_#f5f5f5] [&:-webkit-autofill]:[-webkit-text-fill-color:#181818] [&:-webkit-autofill:focus]:shadow-[inset_0_0_0_1000px_#f5f5f5] [&:-webkit-autofill:hover]:shadow-[inset_0_0_0_1000px_#f5f5f5]"
                    />
                    <button
                        type="submit"
                        aria-label="Continue with email"
                        className={cn(
                            'flex size-11.25 shrink-0 items-center justify-center rounded-[1rem] text-white transition-colors',
                            emailReady
                                ? 'cursor-pointer bg-[#181818] hover:bg-[#2c2c2c]'
                                : 'cursor-default bg-[#979797]',
                        )}
                    >
                        {detecting || isEnteringStep ? (
                            <Spinner className="size-5 text-white" />
                        ) : (
                            <ArrowRight className="w-5" />
                        )}
                    </button>
                </div>
                {emailError && (
                    <p role="alert" className="mt-2 text-sm text-destructive">
                        {emailError}
                    </p>
                )}
            </form>
        </>
    );

    const renderForcedSocial = () => (
        <>
            <p className="mt-8 text-[0.9375rem] text-[#656565]">
                Looks like you created your account using {forcedProvider}.
                We&rsquo;ll use this to log you in.
            </p>
            <AuthModalSocialButtons
                className="mt-5"
                loginOptions={
                    policyForcesGoogle
                        ? ['Google']
                        : publicUser?.socialLoginOptions
                }
                authSource={authSource}
                redirect={redirect}
                skipPostSignUp={!!skipPostSignUp}
            />
            <div className="mt-5 text-[0.875rem] text-[#828282]">
                <p>
                    Not you?{' '}
                    <button
                        type="button"
                        className="underline"
                        onClick={handleUseDifferentEmail}
                    >
                        Use another account
                    </button>
                </p>
                {/* Both concern the password path, so they appear together
                    and are withheld together. Under the staff policy there is
                    deliberately no password to reach, and offering a reset for
                    one would be a dead end. */}
                {!policyForcesGoogle && (
                    <>
                        <p className="mt-2">
                            Or,{' '}
                            <button
                                type="button"
                                className="underline"
                                onClick={() => setForcedSocialBypassed(true)}
                            >
                                log in with password
                            </button>
                        </p>
                        <p className="mt-2">
                            <button
                                type="button"
                                className="underline"
                                onClick={() =>
                                    startStepTransition(() => {
                                        setStep('forgot-password');
                                    })
                                }
                            >
                                Forgot password?
                            </button>
                        </p>
                    </>
                )}
            </div>
        </>
    );

    const renderDetailsStep = () => (
        <>
            {/* Leading, not trailing. Below the form this sat under "Forgot
                password?" and "Email me a code", where it read as a third
                alternative action rather than as the way back out of the step —
                the same reason the forgot-password step puts its arrow here.
                An arrow also carries both faces of this step: "Not me?" only
                made sense where we had claimed to recognise someone.

                The code step shows it too. It is the same step wearing a
                different heading, and the arrow is how every other step in the
                gate is left — skipping it here left the one screen you reach by
                accident as the one with no way back at the top. Each step keeps
                its worded escape below the action as well, so the pair reads the
                same throughout. */}
            {enteredViaEmail && (
                <button
                    type="button"
                    aria-label="Back"
                    onClick={
                        emailCodeActive
                            ? () => setEmailCodeActive(false)
                            : handleUseDifferentEmail
                    }
                    className="mb-4 flex size-8 items-center justify-center self-start rounded-[0.75rem] text-[#181818] hover:bg-black/5"
                >
                    <ArrowLeftLine className="size-5" />
                </button>
            )}
            <GateHeading>
                {emailCodeActive ? 'Enter your code' : detailsHeadline}
            </GateHeading>
            <GateSubcopy>
                {emailCodeActive ? (
                    <>
                        {`We sent a 6-digit code to ${knownEmail}`}
                        {emailCodeSending && (
                            <Spinner className="ml-2 inline-block size-4 align-[-0.125rem] text-[#828282]" />
                        )}
                    </>
                ) : (
                    detailsSubcopy
                )}
            </GateSubcopy>
            {!emailCodeActive &&
                publicUser?.type === 'existing' &&
                renderPasskeyNotice()}
            {forcedSocial ? (
                renderForcedSocial()
            ) : (
                <>
                    {!enteredViaEmail && (
                        <>
                            <AuthModalSocialButtons
                                className="mt-10 pt-1"
                                loginOptions={publicUser?.socialLoginOptions}
                                authSource={authSource}
                                redirect={redirect}
                                skipPostSignUp={!!skipPostSignUp}
                            />
                            <div className="mt-6 flex items-center justify-center gap-3 opacity-60">
                                <span className="h-px w-14 bg-[#9d9d9d]" />
                                <span className="text-[1.0625rem] tracking-[-0.03em] text-[#828282]">
                                    or continue with your email
                                </span>
                                <span className="h-px w-14 bg-[#9d9d9d]" />
                            </div>
                        </>
                    )}
                    {/* One rhythm down the column: the form sits the same
                        distance below the subcopy as the subcopy does below the
                        heading. The wider `mt-6` below is measured from the
                        divider instead, which carries its own space. */}
                    <div className={cn(enteredViaEmail ? 'mt-4' : 'mt-6')}>
                        <LoginSignUpForm
                            variant="rocco"
                            flow={derivedFlow}
                            authSource={authSource}
                            defaultPublicUser={publicUser}
                            defaultEmail={submittedEmail}
                            emailCodeStyle={isLoginFlow ? 'button' : 'link'}
                            redirect={redirect}
                            allowExternalUrls={allowExternalUrls}
                            skipPostSignUp={!!skipPostSignUp}
                            skipAuthRedirect={skipAuthRedirect}
                            onEmailBlur={handleUserExists}
                            // Controlled, so the heading and the back arrow above
                            // the form can leave the code step — observing it
                            // would only tell us we were on it.
                            emailCodeStepActive={emailCodeActive}
                            onEmailCodeStepActiveChange={setEmailCodeActive}
                            onEmailCodeStepChromeChange={(state) =>
                                setEmailCodeSending(state.sendingCode)
                            }
                            onSuccess={handleFormSuccess}
                            onAuthComplete={handleAuthComplete}
                            onResetPassword={(e) => {
                                e.preventDefault();
                                startStepTransition(() => {
                                    setForgotCompleted(false);
                                    setStep('forgot-password');
                                });
                            }}
                        />
                    </div>
                    {isSignUpFlow && (
                        <p className="mt-4 text-center text-[0.8125rem] text-[#828282]">
                            By signing up, you have read and agree to our{' '}
                            <a
                                href={byCountry(POLICY_LINKS)}
                                target="_blank"
                                rel="noreferrer"
                                className="underline"
                            >
                                Privacy Policy
                            </a>
                        </p>
                    )}
                </>
            )}
        </>
    );

    const renderForgotPasswordStep = () => (
        <>
            <button
                type="button"
                aria-label="Back to log in"
                onClick={() => {
                    startStepTransition(() => {
                        setStep('details');
                        setForgotCompleted(false);
                    });
                }}
                className="flex size-8 items-center justify-center self-start rounded-[0.75rem] text-[#181818] hover:bg-black/5"
            >
                <ArrowLeftLine className="size-5" />
            </button>
            <GateHeading className="mt-4">
                {forgotCompleted ? (
                    <>
                        Reset password link sent
                        {/* Inline rather than a flex row like the enquiry
                            screen's: this heading wraps, and a flex check would
                            sit centred against the whole block instead of after
                            the last word. */}
                        <Check
                            aria-hidden
                            className="ml-2.5 inline-block size-6 shrink-0 align-middle"
                            strokeWidth={2}
                        />
                    </>
                ) : (
                    'Forgot password?'
                )}
            </GateHeading>
            <div className="mt-6">
                <ForgotPasswordForm
                    variant="rocco"
                    flow={
                        forgotCompleted
                            ? 'forgot-password-completed'
                            : 'forgot-password'
                    }
                    onBack={() => {
                        startStepTransition(() => {
                            setStep('details');
                            setForgotCompleted(false);
                        });
                    }}
                    onSuccess={() => setForgotCompleted(true)}
                />
            </div>
        </>
    );

    if (step === 'passkey-login') {
        return (
            <PasskeyLoginStep
                authSource={authSource}
                firstName={passkeyHintRef.current?.firstName}
                refreshOnSuccess={!!skipAuthRedirect}
                onSuccess={handlePasskeyLoginSuccess}
                onFallback={handlePasskeyLoginFallback}
            />
        );
    }

    if (step === 'secure-sign-in' && enrolMint) {
        return (
            <SecureSignInOffer
                mint={enrolMint}
                authSource={authSource}
                firstName={
                    publicUser?.type === 'existing'
                        ? publicUser.firstName || undefined
                        : undefined
                }
                memberID={isLoggedIn ? user.ID : undefined}
                onEnrolled={() => {
                    askEnrolledRef.current = true;
                }}
                onDone={finishWithHoldingStep}
            />
        );
    }

    // `secure-sign-in` without a mint cannot happen — the step is only entered
    // with one — but the hold is the right answer to it either way: the flow is
    // between auth and its destination.
    if (step === 'redirecting' || step === 'secure-sign-in') {
        return (
            // `flex-1` fills the desktop card, whose pane is a fixed 635px. The
            // floor is for the hosts sized by their content instead — the
            // mobile sheet and the `/login` card — where a spinner on its own
            // measures about its 36px and would drop the surface to a strip for
            // the length of the redirect. 75 = 300px; see the two-pane surface
            // for why these are scale steps rather than arbitrary lengths.
            <div className="flex min-h-75 flex-1 items-center justify-center">
                <Spinner className="size-9 text-[#828282]" />
            </div>
        );
    }

    return (
        <div className="mx-auto my-auto flex w-full max-w-90.75 flex-col">
            {step === 'initial' && renderInitialStep()}
            {step === 'details' && renderDetailsStep()}
            {step === 'forgot-password' && renderForgotPasswordStep()}
        </div>
    );
};
